import * as React from "react";
import { observer } from "mobx-react";
import { CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";
import Offers = CampaignRecommendationsWithOffersQuery.Offers;
import { locKeys } from "components/shared/Merchandising/Inventory/l10n";
import { useLocalization } from "@shared-ui/localization-context";
import { withStores } from "stores";
import { RouteStore, QueryRoute } from "bernie-core";
import { Layer } from "bernie-view";
import { InfoDialog } from "../../Common/InfoDialog/InfoDialog";
import { MabTrackEvent, withMabTracker } from "src/components/utility/analytics/MabTracker";
import { TallHotelInventoryCard } from "../../Inventory/TallHotelInventoryCard/TallHotelInventoryCard";
import { CompactHotelInventoryCard } from "../../Inventory/CompactHotelInventoryCard/CompactHotelInventoryCard";

export interface HotelInventoryCardProps {
  offer: Offers;
  cardIndex: number;
  campaignId: string;
  openDialog: (dialogPath: string) => void;
  closeDialog: () => void;
  moduleName: string;
  withBorder?: boolean;
  routeStore: RouteStore;
  mabTrack?: (x: MabTrackEvent) => null;
  hotelInventoryView: string;
}

export const HotelInventoryCard = withMabTracker(
  withStores("routeStore")(
    observer(
      ({
        offer,
        cardIndex,
        campaignId,
        closeDialog,
        moduleName,
        withBorder,
        routeStore,
        mabTrack,
        hotelInventoryView,
      }: HotelInventoryCardProps) => {
        if (offer.__typename !== "HotelOfferItem") {
          return null;
        }
        const { formatText } = useLocalization();
        const strikeThroughPrice = offer?.lodgingOffer?.strikeThroughPrice?.value;
        const currentPrice = offer.lodgingOffer?.price?.value;
        const a11yPrice = offer.lodgingOffer?.price?.allyText || "";

        const priceDisclaimerLabel = formatText(locKeys.priceDisclaimerLabel);

        const disclaimerInfoDialog = (): JSX.Element => {
          return (
            <QueryRoute query={{ pwaDialog: `disclaimer-info-${campaignId}-${cardIndex}` }}>
              <Layer id={`disclaimer-info-dialog-${campaignId}-${cardIndex}`} onDismiss={closeDialog}>
                <InfoDialog
                  infoText={offer?.lodgingOffer?.price.disclaimerText}
                  closeDialog={closeDialog}
                  buttonText={formatText("DestinationsByAffinity.priceDialogCloseButton")}
                />
              </Layer>
            </QueryRoute>
          );
        };

        const cardClicked = mabTrack
          ? () => {
              const mabTrackData: MabTrackEvent = {
                moduleName: "geo_hotels_1",
                eventTarget: "geo_hotels_1",
                eventType: "homepageClick.nonPath",
                rfrr: `MerchCampaign.card-${campaignId}-${cardIndex}`,
              };
              mabTrack(mabTrackData);
            }
          : undefined;
        const isTallHotelView = hotelInventoryView === "tall";

        return isTallHotelView ? (
          <TallHotelInventoryCard
            offer={offer}
            cardIndex={cardIndex}
            campaignId={campaignId}
            moduleName={moduleName}
            withBorder={withBorder}
            routeStore={routeStore}
            priceDisclaimerLabel={priceDisclaimerLabel}
            strikeThroughPrice={strikeThroughPrice}
            disclaimerInfoDialog={disclaimerInfoDialog}
            currentPrice={currentPrice}
            a11yPrice={a11yPrice}
            cardClicked={cardClicked}
          />
        ) : (
          <CompactHotelInventoryCard
            offer={offer}
            cardIndex={cardIndex}
            campaignId={campaignId}
            moduleName={moduleName}
            withBorder={withBorder}
            routeStore={routeStore}
            priceDisclaimerLabel={priceDisclaimerLabel}
            strikeThroughPrice={strikeThroughPrice}
            disclaimerInfoDialog={disclaimerInfoDialog}
            currentPrice={currentPrice}
            a11yPrice={a11yPrice}
            cardClicked={cardClicked}
          />
        );
      }
    )
  )
);
